import { useEffect, useState } from 'react';
import { CaretDown, CaretRight, Clock, Package } from 'shared/icons';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import { DateText, Mono } from '../../ui/Mono';
import { ListRepackCandidates } from './repackApi';
import { FormatQty } from './repackModel';

/**
 * AVISO DE FIFO — **e ele NÃO bloqueia nada** (§6.2, que nega a trava com todas
 * as letras: "o seletor ordena por FIFO e AVISA ao escolher um mais novo
 * havendo mais antigo do mesmo produto. Nunca bloqueia").
 *
 * ── A decisão de interação ──────────────────────────────────────────────────
 * O risco de um aviso que aparece toda hora é o chão aprender a ignorá-lo. Três
 * escolhas para ele avisar sem atrapalhar:
 *
 * 1. **Cor e lugar de INFORMAÇÃO, não de erro.** Âmbar, dentro do fluxo, acima
 *    do botão de confirmar — nunca modal, nunca vermelho, nunca no caminho do
 *    clique. Vermelho é o contrato do coletor para "você fez algo que o sistema
 *    não aceita"; gastar essa cor aqui ensinaria a ignorá-la no dia em que ela
 *    significar de verdade.
 * 2. **O botão de confirmar continua HABILITADO**, e o texto diz isso em
 *    português. Um aviso que trava é uma trava mal explicada.
 * 3. **O aviso é ÚTIL**: ele abre a lista FIFO do produto (`fifo_rank` 1 = o
 *    mais antigo) e oferece TROCAR o pallet de origem com um clique. Assim
 *    quem quis obedecer o FIFO obedece sem sair da tela, e quem tem motivo para
 *    furar a fila segue sem pedir licença a ninguém.
 */

const CandidateList = ({ productId, chosenCode, label, onUseLpn }) => {
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setLoadError(null);
        ListRepackCandidates({ product_id: productId, limit: 20 })
            .then((data) => {
                if (alive) {
                    setRows(Array.isArray(data?.results) ? data.results : []);
                }
            })
            .catch((error) => {
                if (alive) {
                    setLoadError(error?.message || label('wms.common.load_error'));
                }
            })
            .finally(() => {
                if (alive) {
                    setLoading(false);
                }
            });
        return () => {
            alive = false;
        };
    }, [productId, label]);

    if (loading) {
        return <p className="px-1 py-2 text-xs text-warning-text dark:text-warning-300">…</p>;
    }
    if (loadError) {
        return <p className="px-1 py-2 text-xs text-warning-text dark:text-warning-300">{loadError}</p>;
    }
    if (!rows || rows.length === 0) {
        return (
            <p className="px-1 py-2 text-xs text-warning-text dark:text-warning-300">
                {label('wms.repack.fifo_empty')}
            </p>
        );
    }

    return (
        <ul className="mt-2 space-y-1">
            {rows.map((row) => {
                const isChosen = String(row.code) === String(chosenCode);
                return (
                    <li
                        key={`${row.id}-${row.supplier_lot ?? ''}`}
                        className="flex flex-wrap items-center gap-2 rounded-lg bg-white/70 px-2.5 py-1.5 text-xs dark:bg-mono-950/50"
                    >
                        <Package size={14} className="shrink-0 text-mono-500" />
                        <Mono className="font-semibold text-black dark:text-white">{row.code}</Mono>
                        {row.fifo_rank === 1 ? <Badge variant="info">{label('wms.repack.fifo_oldest')}</Badge> : null}
                        <span className="text-mono-500 dark:text-mono-400">
                            <DateText value={row.created_at} className="text-xs" />
                        </span>
                        {row.location_code ? <Mono className="text-mono-500">{row.location_code}</Mono> : null}
                        {row.supplier_lot ? <Mono className="text-mono-500">{row.supplier_lot}</Mono> : null}
                        <Mono className="ml-auto text-mono-600 dark:text-mono-400">{FormatQty(row.qty)}</Mono>
                        {isChosen ? (
                            <Badge variant="default">{label('wms.repack.fifo_chosen')}</Badge>
                        ) : onUseLpn ? (
                            <Button type="button" variant="ghost" size="sm" onClick={() => onUseLpn(row.code)}>
                                {label('wms.repack.fifo_use')}
                            </Button>
                        ) : null}
                    </li>
                );
            })}
        </ul>
    );
};

const FifoNotice = ({ warnings = [], chosenCode = null, label, onUseLpn = null }) => {
    const [openProductId, setOpenProductId] = useState(null);

    if (!warnings.length) {
        return null;
    }

    return (
        <div
            role="status"
            aria-live="polite"
            className="space-y-2 rounded-xl border border-warning-400 bg-warning-soft px-3 py-3 dark:border-warning-700 dark:bg-warning-900/25"
        >
            <div className="flex items-start gap-2">
                <Clock size={18} className="mt-0.5 shrink-0 text-warning-text dark:text-warning-300" />
                <div className="min-w-0">
                    <p className="text-sm font-bold text-warning-text dark:text-warning-200">
                        {label('wms.repack.fifo_title')}
                    </p>
                    <p className="text-xs text-warning-text dark:text-warning-300">{label('wms.repack.fifo_hint')}</p>
                </div>
            </div>

            <ul className="space-y-1.5">
                {warnings.map((warning) => {
                    const isOpen = openProductId === warning.product_id;
                    return (
                        <li key={`${warning.code}-${warning.product_id}`} className="text-xs">
                            <div className="flex flex-wrap items-center gap-2">
                                <Mono className="font-semibold text-warning-text dark:text-warning-200">
                                    {warning.sku || warning.product_id}
                                </Mono>
                                <span className="text-warning-text dark:text-warning-300">
                                    {label('wms.repack.fifo_oldest')}:
                                </span>
                                <Mono className="font-bold text-warning-text dark:text-warning-200">
                                    {warning.oldest_lpn_code}
                                </Mono>
                                <DateText
                                    value={warning.oldest_lpn_created_at}
                                    className="text-xs text-warning-text dark:text-warning-300"
                                />
                                <button
                                    type="button"
                                    onClick={() => setOpenProductId(isOpen ? null : warning.product_id)}
                                    className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-warning-text underline-offset-2 hover:underline dark:text-warning-200"
                                >
                                    {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                                    {isOpen
                                        ? label('wms.repack.fifo_hide_candidates')
                                        : label('wms.repack.fifo_show_candidates')}
                                </button>
                            </div>
                            {isOpen ? (
                                <CandidateList
                                    productId={warning.product_id}
                                    chosenCode={chosenCode ?? warning.chosen_lpn_code}
                                    label={label}
                                    onUseLpn={onUseLpn}
                                />
                            ) : null}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default FifoNotice;
